import {
  type Component,
  For,
  Index,
  Show,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  onCleanup,
} from "solid-js";

import { apiFetch, generateImage } from "@/api/client";
import { fetchConsent, recordConsent } from "@/api/consent";
import {
  createConversation,
  deleteConversation,
  getConversation,
  listConversations,
} from "@/api/conversations";
import type {
  ChatMessage,
  ChatRequest,
  Conversation,
  MessageRecord,
  PersistedToolCall,
} from "@/api/generated";
import { tokenStorage } from "@/auth/storage";
import AttachmentPicker from "./AttachmentPicker";
import AttachmentsLibrary from "./AttachmentsLibrary";
import { useChatStream } from "./ChatStreamProvider";
import ContextUsedDisclosure from "./ContextUsedDisclosure";
import ToolCallCard from "./ToolCallCard";
import { createAttachmentsStore } from "./attachmentsStore";
import {
  RETRY_DEFAULT_PROVIDER,
  type SearchProviderId,
  isSearchProviderId,
  endpointFor,
  formatGroundingResult,
  labelFor,
  normalizeResults,
  serverFor,
  sourcesFrom,
} from "./searchProviders";
import {
  type ChatViewMode,
  buildBrowseContext,
  buildSearchContext,
  compactSearchContext,
  isThinking,
  parseUserContent,
  reasoningPreview,
  resolveChatViewMode,
  retryPlan,
  selectVisibleTurns,
  splitAssistantContent,
  stripImageDataUris,
  summarizeThread,
} from "./chatModel";
import ConsentModal from "./components/ConsentModal";
import ErrorBlock from "./components/ErrorBlock";
import InputShell from "./components/InputShell";
import { shouldAutoFocusComposer } from "./components/composerFocusModel";
import type { SlashItemId } from "./components/slashMenuModel";
import { streamBrowse } from "./browseClient";
import { browseHostOf, parseBrowseDraft } from "./browseModel";
import { dropBrowseWatch, mergeBrowseWatch } from "./browseWatch";
import { consentNeeded } from "./consentModel";
import StructuralRenderer from "./structural/StructuralRenderer";
import type { ChatTurn, ToolCallView } from "./turn";
import {
  ANSWERED_IN,
  type TurnPhase,
  formatElapsed,
  parseSearchTiming,
  phaseLabel,
} from "./turnPhase";
import VoiceRecorder from "./voice/VoiceRecorder";

export interface ChatPageProps {
  threadId: string | null;
  navigateToThread: (threadId: string | null) => void;
  // Called after each chat turn settles (success OR error). App uses
  // this to refresh its `paletteThreads` resource so ⌘K reflects the
  // new/updated conversation. Optional so tests don't need to wire it.
  onTurnSettled?: () => void;
  // True while a modal overlay (⌘K palette / `?` cheat sheet) is open.
  // Gates auto-focus so we don't yank focus out of the open modal.
  // Optional so tests don't need to wire it.
  overlayOpen?: () => boolean;
}

const DEFAULT_MODELS = [
  "gpt-oss-120b",
  "llama3.1:8b",
  "gpt-4o-mini",
  "claude-haiku-4-5-20251001",
] as const;

const ChatPage: Component<ChatPageProps> = (props) => {
  const chatStream = useChatStream();
  const [model, setModel] = createSignal<string>(DEFAULT_MODELS[0]);
  const [draft, setDraft] = createSignal("");
  const [localTurns, setLocalTurns] = createSignal<ChatTurn[]>([]);
  // The thread `localTurns` currently belongs to. Solid's createResource keeps
  // returning the previous thread's transcript while it refetches a new one, so
  // `localTurns` lags a switch — `turns()` gates on this id to avoid painting
  // the old thread's content under the new thread's URL. Kept in sync at every
  // setLocalTurns site that changes which thread the turns represent.
  const [localTurnsThreadId, setLocalTurnsThreadId] = createSignal<
    string | null
  >(null);
  // One-shot mode flags armed via the slash menu. Both reset after a
  // single send so the user can't get stuck in a "stuck on search"
  // state (the previous regression: webSearch latched on forever and
  // /image's empty-draft early-return left the search flag to capture
  // the next Enter, which is why "image gen routed to a web search"
  // was the symptom). At most one is true at a time — selecting one
  // disarms the other.
  // Which provider `/search` is armed for, or null. Replaces the old
  // boolean: TinyFish Search is a provider choice under Search.
  // Agent (`/browse`) is a separate modality flag, like Image.
  const [armedProvider, setArmedProvider] =
    createSignal<SearchProviderId | null>(null);

  // One clock per turn, replacing the two unrelated readouts a turn used to
  // show (a "Thinking" counter for the whole wait, and the tool card's raw
  // ms for a slice of it). `phaseTurnId` scopes it to the turn in flight;
  // every other turn reads "done".
  const [phase, setPhase] = createSignal<TurnPhase>("done");
  const [phaseTurnId, setPhaseTurnId] = createSignal<string | null>(null);
  const [phaseStartedAt, setPhaseStartedAt] = createSignal(0);
  const [phaseProvider, setPhaseProvider] =
    createSignal<SearchProviderId | null>(null);
  // Final elapsed per settled turn, so a slow turn can still say how slow.
  //
  // Keyed by turn id — but `settleTurn` triggers a transcript refetch, and
  // hydration swaps every local id (`a-<ts>`) for the BE's message id. Keying
  // on the local id alone made this row flash for a few hundred ms and then
  // vanish for good. `pendingTotal` carries it across that swap and is
  // re-keyed onto the freshly hydrated assistant turn.
  //
  // Deliberately in-memory only: this is a live-session affordance, and the
  // durable record of where a search turn's time went is the tool call's own
  // persisted timing.
  const [turnTotals, setTurnTotals] = createSignal<Record<string, number>>({});
  const [pendingTotal, setPendingTotal] = createSignal<number | null>(null);
  const [imageModeEnabled, setImageModeEnabled] = createSignal(false);
  const [generatingImage, setGeneratingImage] = createSignal(false);
  const [agentModeEnabled, setAgentModeEnabled] = createSignal(false);
  const [browsing, setBrowsing] = createSignal(false);
  const [browseError, setBrowseError] = createSignal<string | null>(null);
  let browseAbort: AbortController | null = null;

  // PRIV-1: third-party egress consent. We load the user's consent status
  // once, then gate the external-tool actions (Exa search, Gemini image): if
  // consent is enforced and not yet given, `ensureConsent` shows the named
  // modal and resolves true only once the user accepts (and we've recorded it
  // server-side). The backend is the authoritative gate; this is the UX in
  // front of it. `consentStatus` re-fetches if the request fails (returns
  // undefined), in which case `consentNeeded` is conservative and doesn't block.
  const [consentStatus, { mutate: mutateConsent }] = createResource(() =>
    fetchConsent().catch(() => undefined),
  );
  const [pendingConsent, setPendingConsent] = createSignal<{
    provider: string;
    resolve: (accepted: boolean) => void;
  } | null>(null);

  const ensureConsent = (provider: string): Promise<boolean> => {
    if (!consentNeeded(consentStatus(), provider)) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      setPendingConsent({ provider, resolve });
    });
  };

  const acceptConsent = async (): Promise<void> => {
    const pending = pendingConsent();
    if (!pending) return;
    setPendingConsent(null);
    try {
      const updated = await recordConsent(pending.provider);
      mutateConsent(updated);
      pending.resolve(true);
    } catch {
      // Recording failed — don't proceed with the egress action.
      pending.resolve(false);
    }
  };

  const cancelConsent = (): void => {
    const pending = pendingConsent();
    setPendingConsent(null);
    pending?.resolve(false);
  };

  // Session-wide reasoning open state — once the user opens any
  // reasoning row in this chat surface, all subsequent rows render
  // expanded by default (per redesign plan PR-C3 "click expands for
  // the session").
  const [reasoningOpen, setReasoningOpen] = createSignal(false);
  const attachments = createAttachmentsStore();
  // Prefer the live provider stream when it targets the thread we're
  // viewing — otherwise fall back to the BE-hydrated local transcript.
  // This is what lets a navigate-away → navigate-back resume seeing the
  // turns that the in-flight stream is still patching.
  const turns = (): ChatTurn[] => {
    const a = chatStream.active();
    const streamTurns = a && a.threadId === props.threadId ? a.turns : null;
    return selectVisibleTurns(
      streamTurns,
      localTurns(),
      localTurnsThreadId(),
      props.threadId,
    );
  };
  const streaming = (): boolean => {
    const a = chatStream.active();
    return Boolean(a && a.threadId === props.threadId && a.streaming);
  };
  const summary = createMemo(() => summarizeThread(turns()));
  // Guards against the create-conversation race: a second send() entry
  // can sneak in between the `await createConversation` resolving and
  // start() being called. `streaming()` is still false during the
  // network call. A plain ref avoids reactive overhead.
  let creating = false;
  let messagesEl: HTMLOListElement | undefined;
  // Imperative focus() into the composer textarea, captured from
  // InputShell's `focusRef`. Used to keep the composer focused on load
  // and thread-entry so single-key shortcuts (`?`/`/`) type instead of
  // opening the cheat sheet / palette. Reassigned once via InputShell's
  // `focusRef` callback (biome can't see that flow, hence the explicit let).
  let focusComposer: (() => void) | null = null;
  // Captured at AttachmentPicker mount via its `triggerRef` prop. The
  // "upload new" button inside the AttachmentsLibrary popover invokes
  // this to open the OS file picker without rendering the legacy
  // paperclip chip button.
  let attachmentTrigger: (() => void) | null = null;
  // Captured at AttachmentsLibrary mount. The slash menu's `/file`
  // item opens this popover, which exposes both the prior uploads
  // list and the "upload new" action (folded together so the visible
  // folder + paperclip chips are gone).
  let libraryTrigger: (() => void) | null = null;
  const scrollState = { stickToBottom: true };

  // Insert dictated text into the composer. Appends (with a separator) when a
  // draft already exists so voice augments rather than clobbers typed text,
  // then refocuses so the user can edit / send (reuses PR #105 focus plumbing).
  const appendTranscript = (text: string) => {
    const t = text.trim();
    if (!t) return;
    setDraft((d) => (d.trim().length > 0 ? `${d.trimEnd()} ${t}` : t));
    focusComposer?.();
  };

  // Slash menu dispatcher — wires the three locked items (Image,
  // Search, Attach) into their existing handlers. /image and /search
  // are placeholders; the real wiring lands in Phase C alongside
  // structural-intent rendering. /file opens the AttachmentsLibrary
  // popover; from there the user picks "upload new" or re-attaches
  // a prior upload.
  const handleSlashSelect = (id: SlashItemId): void => {
    if (id === "attach") {
      libraryTrigger?.();
      return;
    }
    if (id === "search") {
      // No longer arms directly — InputShell advances to the provider stage
      // and calls handleSelectProvider once the user picks one.
      return;
    }
    if (id === "image") {
      // Arm image-mode for the next send. Previously this called
      // handleGenerateImage() directly, which silently no-op'd on empty
      // draft (the typical case when opening the slash menu first). Now
      // it mirrors /search — user types prompt, hits Enter, send()
      // dispatches to the image path.
      setArmedProvider(null);
      setAgentModeEnabled(false);
      setImageModeEnabled((v) => !v);
      return;
    }
    if (id === "browse") {
      setArmedProvider(null);
      setImageModeEnabled(false);
      setBrowseError(null);
      setAgentModeEnabled((v) => !v);
    }
  };

  // Arm a search provider for the next send. Mutually exclusive with image
  // mode. Deliberately NOT a toggle: in a picker, clicking the already-armed
  // provider reads as "confirm", so toggling it off would silently disarm
  // search and send the next turn ungrounded. Disarming is the chip's `×`.
  const handleSelectProvider = (provider: SearchProviderId): void => {
    setImageModeEnabled(false);
    setAgentModeEnabled(false);
    setArmedProvider(provider);
  };

  // Intentional: NO onCleanup(abort). The stream is owned by the
  // ChatStreamProvider above the router so it survives navigating to
  // /dashboard/etc and keeps writing until BE persistence runs.

  // Persisted threads sidebar. Refetched after create/delete so the
  // sidebar reflects mutations the user just made without a page reload.
  const [threadList, { refetch: refetchThreadList }] =
    createResource(listConversations);

  // Active thread transcript. Keyed on props.threadId so switching
  // threads (or going back to /chat with no id) triggers a fresh load.
  // `null` short-circuits the resource — empty thread state.
  //
  // A 404 (stale URL, deleted thread) is swallowed: we drop the bad
  // id from the URL and fall back to the empty `/chat` state so the
  // next send creates a fresh row instead of failing repeatedly.
  const [transcript, { refetch: refetchTranscript }] = createResource(
    () => props.threadId,
    async (id) => {
      if (!id) return null;
      try {
        return await getConversation(id);
      } catch (err) {
        if (isNotFoundError(err)) {
          props.navigateToThread(null);
          return null;
        }
        throw err;
      }
    },
  );

  // Post-persist refresh, shared by the chat stream's onSettled and the
  // /image handler. Re-sorts the sidebar, refetches the transcript so the
  // hydration effect runs against the BE-persisted turns (the provider /
  // localTurns hold the live view meanwhile), and notifies App so the ⌘K
  // palette's independent thread list refetches too. The /image path needs
  // this just as much as chat: without it the transcript resource keeps its
  // stale pre-persist snapshot (the empty fetch fired right after
  // navigateToThread, before the image existed), and a later hydration
  // re-fire would reset localTurns to [] and blank the generated image.
  const settleTurn = () => {
    const id = phaseTurnId();
    if (id) {
      const total = Date.now() - phaseStartedAt();
      setTurnTotals((m) => ({ ...m, [id]: total }));
      // Survive the hydration that this same function kicks off below.
      setPendingTotal(total);
    }
    setPhase("done");
    setPhaseTurnId(null);
    void refetchThreadList();
    void refetchTranscript();
    props.onTurnSettled?.();
  };

  // Threads we created in this session. The first hydration of any
  // such thread is suppressed because the BE row is empty by design
  // (we're mid-stream into it). Without this guard, the transcript
  // GET fires right after navigateToThread(convId), comes back with
  // messages=[], and the hydration effect would abort the live stream
  // and wipe the just-composed turns. Subsequent visits (reload, nav
  // back) still hydrate normally because the entry is consumed.
  const freshThreadIds = new Set<string>();

  // Clear local state when navigation lands on `/chat` (no thread).
  // Solid's createResource keeps its old value when the source goes
  // falsy, so the hydration effect below wouldn't fire to clear
  // things — without this, +new and browser-back from a thread leave
  // the previous transcript on screen.
  //
  // Aborting an in-flight stream is the caller's job (newThread,
  // openThread, removeThread already do it).
  createEffect(() => {
    if (!props.threadId) {
      setLocalTurns([]);
      setLocalTurnsThreadId(null);
      attachments.reset();
    }
  });

  // Reads the live focus context and grabs the composer when it's safe to
  // do so (see `shouldAutoFocusComposer`). Deferred via queueMicrotask so
  // it runs after InputShell has wired `focusRef` and the DOM has settled.
  // Idempotent: if the textarea already holds focus the model returns
  // false, so re-running this never thrashes focus.
  const autoFocusComposer = () => {
    queueMicrotask(() => {
      const active = document.activeElement;
      const ok = shouldAutoFocusComposer({
        pointerCoarse:
          window.matchMedia?.("(pointer: coarse)")?.matches ?? false,
        overlayOpen: props.overlayOpen?.() ?? false,
        activeElementTag: active instanceof HTMLElement ? active.tagName : null,
        activeElementEditable:
          active instanceof HTMLElement && active.isContentEditable,
      });
      if (ok) focusComposer?.();
    });
  };

  // Auto-focus on mount (fresh load / navigating into chat) and on every
  // thread switch. Tracking `props.threadId` makes this fire initially AND
  // when entering an existing conversation. `overlayOpen` is intentionally
  // read inside the deferred callback, not tracked here, so opening/closing
  // a modal doesn't re-trigger a focus grab — only thread changes do.
  createEffect(() => {
    void props.threadId;
    autoFocusComposer();
  });

  // Hydrate `turns` from the loaded transcript when it arrives.
  // Runs in addition to the reset effect above — this one only fires
  // when transcript() has real data.
  createEffect(() => {
    const detail = transcript();
    const id = props.threadId;
    if (!detail || !id) return;
    // Stale resource value during a thread switch — the new resource
    // hasn't refetched yet and we're seeing the previous thread's
    // data. Skip; the next fire will have the right detail.
    if (detail.conversation.id !== id) return;
    if (
      freshThreadIds.has(detail.conversation.id) &&
      detail.messages.length === 0
    ) {
      // First hydration of a thread we just created — keep local
      // turns + in-flight stream intact. Consume the marker so a
      // future intentional refetch (reload, nav back) hydrates.
      freshThreadIds.delete(detail.conversation.id);
      if (detail.conversation.model) setModel(detail.conversation.model);
      return;
    }
    // If a stream is still in flight for this thread, defer hydration —
    // we don't want a transcript refetch (e.g. sidebar refresh) to
    // overwrite the in-flight turns. The post-settle path below
    // re-hydrates once BE persistence has caught up.
    const active = chatStream.active();
    if (active && active.threadId === id && active.streaming) return;
    if (detail.conversation.model) setModel(detail.conversation.model);
    const hydrated = hydrateTurns(detail.messages);
    setLocalTurns(hydrated);
    // The turn that just settled is the last assistant turn in the refreshed
    // transcript; move its total onto the id it now has.
    const total = pendingTotal();
    if (total !== null) {
      const lastAsst = [...hydrated]
        .reverse()
        .find((t) => t.role === "assistant");
      if (lastAsst) setTurnTotals((m) => ({ ...m, [lastAsst.id]: total }));
      setPendingTotal(null);
    }
    setLocalTurnsThreadId(detail.conversation.id);
    // Stream finished and we just took the BE-hydrated view as truth —
    // drop the provider's snapshot so it can't go stale.
    if (active && active.threadId === id && !active.streaming) {
      chatStream.clear(id);
    }
  });

  const isAtBottom = () => {
    if (!messagesEl) return true;
    return (
      messagesEl.scrollHeight - messagesEl.scrollTop - messagesEl.clientHeight <
      8
    );
  };

  createEffect(() => {
    void turns();
    if (!messagesEl) return;
    if (scrollState.stickToBottom) {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    }
  });

  const send = async () => {
    if (streaming() || creating || generatingImage() || browsing()) return;
    const text = draft().trim();
    if (text.length === 0) return;

    // Image mode dispatches to the Gemini image-gen handler, not the
    // chat stream. Disarm the flag up-front so a failure mid-flight
    // doesn't leave the user stuck in image mode silently.
    if (agentModeEnabled()) {
      if (!(await ensureConsent("tinyfish_agent"))) {
        return;
      }
      const parsed = parseBrowseDraft(text);
      if (!parsed.ok) {
        setBrowseError(parsed.error);
        return;
      }
      setBrowseError(null);
      setAgentModeEnabled(false);
      await handleBrowse(parsed.urls, parsed.goal, text);
      return;
    }

    if (imageModeEnabled()) {
      // PRIV-1: Gemini consent before sending the prompt off-premises. On
      // decline, disarm and abort (the draft stays so the user can retry or
      // send it to the local model instead).
      if (!(await ensureConsent("gemini"))) {
        setImageModeEnabled(false);
        return;
      }
      setImageModeEnabled(false);
      await handleGenerateImage();
      return;
    }

    // Capture + disarm the search flag up-front. Reading it later in
    // the function would race the disarm; capturing here gives us a
    // stable value for the rest of send() while ensuring the chip
    // clears immediately on Enter.
    const provider = armedProvider();
    // PRIV-1: consent for the *chosen* provider before the query leaves the
    // platform — Exa consent must not authorize TinyFish egress, which the
    // BE enforces independently. On decline, keep the chip + draft armed so
    // the user can retry or disarm.
    if (provider && !(await ensureConsent(provider))) {
      return;
    }
    if (provider) setArmedProvider(null);

    // First send of a new thread: create the conversation row up-front
    // so the BE persists messages to it. Title derives from the user's
    // prompt (capped at 80 chars) until we add LLM-generated titles.
    // Failure here aborts the send — better to surface "couldn't save"
    // than to drop a message silently.
    let convId = props.threadId;
    if (!convId) {
      creating = true;
      try {
        const conv = await createConversation({
          title: deriveThreadTitle(text),
          model: model(),
        });
        convId = conv.id;
        // Mark BEFORE navigate so the hydration effect, which fires
        // asynchronously after the resource refetches, sees the entry.
        freshThreadIds.add(convId);
        props.navigateToThread(convId);
        void refetchThreadList();
      } catch (err) {
        // Show the failure inline in a temporary assistant turn — the
        // user just composed a message, the sidebar isn't the place to
        // surface "save failed". They can retry by pressing send again.
        const msg = err instanceof Error ? err.message : String(err);
        setLocalTurns((arr) => [
          ...arr,
          {
            id: `u-${Date.now()}`,
            role: "user",
            content: text,
            reasoning: "",
            pending: false,
            tool_calls: [],
            retrieved_chunk_ids: [],
          },
          {
            id: `e-${Date.now()}`,
            role: "assistant",
            content: "",
            reasoning: "",
            pending: false,
            tool_calls: [],
            error: `could not create thread: ${msg}`,
            retrieved_chunk_ids: [],
          },
        ]);
        // Create failed → still on /chat (navigate never ran); these turns
        // belong to the no-thread state.
        setLocalTurnsThreadId(props.threadId);
        setDraft("");
        return;
      } finally {
        creating = false;
      }
    }

    const userTurn: ChatTurn = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
      reasoning: "",
      pending: false,
      tool_calls: [],
      retrieved_chunk_ids: [],
    };

    const searchToolCallId = `s-${Date.now()}`;
    const asstTurn: ChatTurn = {
      id: `a-${Date.now()}`,
      role: "assistant",
      content: "",
      reasoning: "",
      pending: true,
      tool_calls: provider
        ? [
            {
              id: searchToolCallId,
              name: "web.search",
              server: serverFor(provider),
              args: JSON.stringify({ query: text }),
              status: "pending",
            },
          ]
        : [],
      retrieved_chunk_ids: [],
    };

    // Snapshot the conversation history BEFORE we add the in-flight
    // shells so the BE request only carries committed prior turns.
    const priorTurns = turns();
    setPhaseTurnId(asstTurn.id);
    setPhaseProvider(provider);
    setPhaseStartedAt(Date.now());
    setPhase(provider ? "searching" : "thinking");
    setLocalTurns((arr) => [...arr, userTurn, asstTurn]);
    setLocalTurnsThreadId(convId);
    setDraft("");

    let contextText = "";
    if (provider) {
      const startTime = Date.now();
      try {
        const data = await apiFetch<{ results?: unknown; timing?: unknown }>(
          endpointFor(provider),
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query: text }),
          },
        );

        // One shape from either provider — Exa highlights/excerpt, or a Web
        // Agent page. See normalizeResults for the per-provider rules.
        const results = normalizeResults(provider, data.results);
        const formatted = results
          .map((r, i) => formatGroundingResult(i + 1, r, provider))
          .join("\n---\n\n");

        // Citations for the replay travel as a machine-readable payload
        // inside the block (see buildSearchContext) — never re-parsed out of
        // the result prose, which is third-party page text.
        setPhase("thinking");
        // Citations carry the same numbers the prose above uses, and the
        // block records which provider produced it so a retry can re-run the
        // same search rather than guessing.
        contextText = buildSearchContext(
          formatted,
          sourcesFrom(results),
          provider,
        );

        asstTurn.tool_calls = [
          {
            id: searchToolCallId,
            name: "web.search",
            server: serverFor(provider),
            args: JSON.stringify({ query: text }),
            status: "ok",
            result: {
              // The server's split of the round trip. A slow search and slow
              // page reads are different problems, and a single opaque
              // duration could not tell them apart.
              timing: parseSearchTiming(data.timing) ?? undefined,
              sources: results.map((r) => ({ title: r.title, url: r.url })),
            },
            latency_ms: Date.now() - startTime,
          },
        ];
        setLocalTurns((arr) => {
          const copy = [...arr];
          copy[copy.length - 1] = { ...asstTurn };
          return copy;
        });
      } catch (err: unknown) {
        setPhase("thinking");
        let errMsg = err instanceof Error ? err.message : String(err);
        if (err && typeof err === "object") {
          const errObj = err as Record<string, unknown>;
          if ("message" in errObj && typeof errObj.message === "string") {
            errMsg = errObj.message;
          } else if ("error" in errObj && typeof errObj.error === "string") {
            errMsg = errObj.error;
          }
        }
        asstTurn.tool_calls = [
          {
            id: searchToolCallId,
            name: "web.search",
            server: serverFor(provider),
            args: JSON.stringify({ query: text }),
            status: "error",
            error: errMsg,
            latency_ms: Date.now() - startTime,
          },
        ];
        setLocalTurns((arr) => {
          const copy = [...arr];
          copy[copy.length - 1] = { ...asstTurn };
          return copy;
        });
      }
    }

    const seedTurns = [...priorTurns, userTurn, asstTurn];

    const messages: ChatMessage[] = [];

    // System prompt (identity + answer-first + scannability rules) is
    // owned by the BE — see `ANSWER_FIRST_SYSTEM_PROMPT` in
    // `crates/z01-control-plane/src/handlers/chat_loop.rs`. The BE
    // injects it iff the caller didn't supply one, so do NOT send a
    // system message from here — it would shadow the BE injection.

    messages.push(
      ...priorTurns.map((t) => ({
        role: t.role,
        // Sanitize prior turns before replay:
        //  - strip generated-image data: URIs — the raw base64 would blow
        //    the LLM context / break the harmony parser (stripImageDataUris)
        //  - swap spliced-in Exa results for their citation list — a
        //    searched turn otherwise carries ~6 results of page text for the
        //    rest of the thread, but dropping it outright left follow-ups
        //    like "what was the second result's URL?" unanswerable
        //    (compactSearchContext)
        // Only user turns can carry a block; assistant replies are replayed
        // verbatim so one that happens to open with a marker is not truncated.
        // Display and persistence keep the originals; only the LLM-bound
        // copy is sanitized.
        content:
          t.role === "user"
            ? compactSearchContext(stripImageDataUris(t.content))
            : stripImageDataUris(t.content),
        tool_calls: [],
      })),
    );
    messages.push({
      role: "user",
      content: contextText + text,
      tool_calls: [],
    });
    const tokens = tokenStorage.load();
    const req: ChatRequest = {
      model: model(),
      messages,
      tools: [],
      conversation_id: convId,
      attachment_ids: attachments.readyIds(),
      // The search ran through its own route before this stream opened, so
      // the BE never sees it as a provider tool call. Send it along to be
      // persisted against the assistant message, otherwise the card exists
      // only until the transcript is refetched.
      client_tool_calls: asstTurn.tool_calls.map((c) => ({
        id: c.id,
        name: c.name,
        server: c.server,
        args: c.args,
        status: c.status,
        result: c.result === undefined ? undefined : JSON.stringify(c.result),
        error: c.error,
        latency_ms: c.latency_ms,
      })),
    };
    // Reset pending attachments now that they're committed to the
    // request — keeps the composer clean for the next turn. The chips
    // disappear immediately, the BE-side attachments remain.
    //
    // Note: the no-threadId effect above also calls `attachments.reset()`,
    // but only when `props.threadId` becomes null. Here `convId` is set
    // (either pre-existing or just created), so that effect doesn't
    // fire. The two reset paths cover distinct lifecycle moments.
    attachments.reset();

    chatStream.start({
      threadId: convId,
      seedTurns,
      asstTurnId: asstTurn.id,
      req,
      token: tokens?.access_token,
      onSettled: settleTurn,
    });
  };

  const newThread = () => {
    if (streaming()) chatStream.stop();
    props.navigateToThread(null);
  };

  const openThread = (id: string) => {
    if (streaming()) chatStream.stop();
    if (id === props.threadId) return;
    props.navigateToThread(id);
  };

  const removeThread = async (id: string) => {
    // If the user deletes the thread that's currently streaming, abort
    // eagerly before the BE row vanishes — otherwise the SSE keeps
    // writing into a doomed conversation_id.
    const active = chatStream.active();
    if (active && active.threadId === id && active.streaming) {
      chatStream.stop();
    }
    try {
      await deleteConversation(id);
    } catch {
      // Best-effort: the refetch below resyncs sidebar state if the
      // row survived. No toast UI yet to surface the failure.
    }
    chatStream.clear(id);
    if (id === props.threadId) {
      props.navigateToThread(null);
    }
    void refetchThreadList();
  };

  const handleGenerateImage = async () => {
    const text = draft().trim();
    if (!text) return;

    setGeneratingImage(true);
    // Optimistically add user turn
    const userTurn: ChatTurn = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
      reasoning: "",
      pending: false,
      tool_calls: [],
      retrieved_chunk_ids: [],
    };

    // Add pending assistant turn
    const asstTurnId = `a-${Date.now()}`;
    const asstTurn: ChatTurn = {
      id: asstTurnId,
      role: "assistant",
      content: "generating image...",
      reasoning: "",
      pending: true,
      tool_calls: [],
      retrieved_chunk_ids: [],
    };

    setLocalTurns((prev) => [...prev, userTurn, asstTurn]);
    setLocalTurnsThreadId(props.threadId);
    setDraft("");

    let convId = props.threadId;
    if (!convId) {
      creating = true;
      try {
        const conv = await createConversation({
          title: deriveThreadTitle(text),
          model: model(),
        });
        convId = conv.id;
        freshThreadIds.add(convId);
        props.navigateToThread(convId);
        // Image-gen owns localTurns directly (no stream), so re-anchor them to
        // the new thread id — otherwise the turns() guard would treat them as
        // stale once props.threadId flips and the "generating…" turn vanishes.
        setLocalTurnsThreadId(convId);
        void refetchThreadList();
      } catch (err) {
        setGeneratingImage(false);
        const msg = err instanceof Error ? err.message : JSON.stringify(err);
        setLocalTurns((prev) =>
          prev.map((t) =>
            t.id === asstTurnId
              ? { ...t, pending: false, error: msg, content: "" }
              : t,
          ),
        );
        creating = false;
        return;
      } finally {
        creating = false;
      }
    }

    try {
      const response = await generateImage(text, convId);

      setLocalTurns((prev) =>
        prev.map((t) =>
          t.id === asstTurnId
            ? {
                ...t,
                pending: false,
                content: response.markdown,
              }
            : t,
        ),
      );
      // BE persisted the user + image turns — sync the transcript resource so
      // it no longer holds the stale pre-persist (empty) snapshot.
      settleTurn();
    } catch (err) {
      const msg = err instanceof Error ? err.message : JSON.stringify(err);
      setLocalTurns((prev) =>
        prev.map((t) =>
          t.id === asstTurnId
            ? { ...t, pending: false, error: msg, content: "" }
            : t,
        ),
      );
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleBrowse = async (urls: string[], goal: string, prompt: string) => {
    setBrowsing(true);
    browseAbort = new AbortController();
    const started = Date.now();
    const priorTurns = turns();
    const userTurn: ChatTurn = {
      id: `u-${Date.now()}`,
      role: "user",
      content: prompt,
      reasoning: "",
      pending: false,
      tool_calls: [],
      retrieved_chunk_ids: [],
    };
    const asstTurnId = `a-${Date.now()}`;
    const toolId = `browse-${Date.now()}`;
    const asstTurn: ChatTurn = {
      id: asstTurnId,
      role: "assistant",
      content: "",
      reasoning: "",
      pending: true,
      tool_calls: [
        {
          id: toolId,
          name: "web.browse",
          server: "tinyfish.ai",
          args: JSON.stringify({ urls, goal }),
          status: "pending",
          watch: urls
            .map((u) => ({ host: browseHostOf(u) }))
            .filter((w) => w.host),
        },
      ],
      retrieved_chunk_ids: [],
    };
    setPhaseTurnId(asstTurnId);
    setPhaseProvider(null);
    setPhaseStartedAt(started);
    setPhase("browsing");
    setLocalTurns((prev) => [...prev, userTurn, asstTurn]);
    setLocalTurnsThreadId(props.threadId);
    setDraft("");

    let convId = props.threadId;
    if (!convId) {
      creating = true;
      try {
        const conv = await createConversation({
          title: deriveThreadTitle(prompt),
          model: model(),
        });
        convId = conv.id;
        freshThreadIds.add(convId);
        props.navigateToThread(convId);
        setLocalTurnsThreadId(convId);
        void refetchThreadList();
      } catch (err) {
        setBrowsing(false);
        const msg = err instanceof Error ? err.message : String(err);
        setLocalTurns((prev) =>
          prev.map((t) =>
            t.id === asstTurnId
              ? { ...t, pending: false, error: msg }
              : t,
          ),
        );
        creating = false;
        return;
      } finally {
        creating = false;
      }
    }

    const patchTool = (patch: Partial<ToolCallView>) => {
      setLocalTurns((prev) =>
        prev.map((t) =>
          t.id === asstTurnId
            ? {
                ...t,
                tool_calls: t.tool_calls.map((c) =>
                  c.id === toolId ? { ...c, ...patch } : c,
                ),
              }
            : t,
        ),
      );
    };

    try {
      for await (const ev of streamBrowse(
        { urls, goal, prompt, conversationId: convId },
        browseAbort.signal,
      )) {
        if (ev.kind === "started") {
          setLocalTurns((prev) =>
            prev.map((t) =>
              t.id === asstTurnId
                ? {
                    ...t,
                    tool_calls: t.tool_calls.map((c) =>
                      c.id === toolId
                        ? {
                            ...c,
                            watch: mergeBrowseWatch(c.watch, { host: ev.host }),
                          }
                        : c,
                    ),
                  }
                : t,
            ),
          );
        } else if (ev.kind === "progress") {
          patchTool({
            args: JSON.stringify({
              urls,
              host: ev.host,
              purpose: ev.purpose,
            }),
          });
        } else if (ev.kind === "watch") {
          setLocalTurns((prev) =>
            prev.map((t) =>
              t.id === asstTurnId
                ? {
                    ...t,
                    tool_calls: t.tool_calls.map((c) =>
                      c.id === toolId
                        ? {
                            ...c,
                            watch: mergeBrowseWatch(c.watch, {
                              host: ev.host,
                              url: ev.url,
                            }),
                          }
                        : c,
                    ),
                  }
                : t,
            ),
          );
        } else if (ev.kind === "site_done") {
          setLocalTurns((prev) =>
            prev.map((t) =>
              t.id === asstTurnId
                ? {
                    ...t,
                    tool_calls: t.tool_calls.map((c) =>
                      c.id === toolId
                        ? { ...c, watch: dropBrowseWatch(c.watch, ev.host) }
                        : c,
                    ),
                  }
                : t,
            ),
          );
        } else if (ev.kind === "complete") {
          const latency = Date.now() - started;
          const grounded = {
            ...asstTurn,
            pending: true,
            content: "",
            tool_calls: asstTurn.tool_calls.map((c) =>
              c.id === toolId
                ? {
                    ...c,
                    status: "ok" as const,
                    latency_ms: latency,
                    result: { urls, markdown: ev.markdown },
                  }
                : c,
            ),
          };
          patchTool({
            status: "ok",
            latency_ms: latency,
            result: { urls, markdown: ev.markdown },
          });
          setPhase("thinking");
          const contextText = buildBrowseContext(ev.markdown, urls);
          const seedTurns = [...priorTurns, userTurn, grounded];
          const messages: ChatMessage[] = [
            ...priorTurns.map((t) => ({
              role: t.role,
              content:
                t.role === "user"
                  ? compactSearchContext(stripImageDataUris(t.content))
                  : stripImageDataUris(t.content),
              tool_calls: [],
            })),
            {
              role: "user",
              content: contextText + prompt,
              tool_calls: [],
            },
          ];
          const tokens = tokenStorage.load();
          const req: ChatRequest = {
            model: model(),
            messages,
            tools: [],
            conversation_id: convId,
            attachment_ids: [],
            client_tool_calls: grounded.tool_calls.map((c) => ({
              id: c.id,
              name: c.name,
              server: c.server,
              args: c.args,
              status: c.status,
              result: c.result === undefined ? undefined : JSON.stringify(c.result),
              error: c.error,
              latency_ms: c.latency_ms,
            })),
          };
          setLocalTurns((prev) =>
            prev.map((t) => (t.id === asstTurnId ? { ...grounded } : t)),
          );
          chatStream.start({
            threadId: convId,
            seedTurns,
            asstTurnId,
            req,
            token: tokens?.access_token,
            onSettled: settleTurn,
          });
          return;
        } else if (ev.kind === "error") {
          const latency = Date.now() - started;
          patchTool({ status: "error", error: ev.message, latency_ms: latency });
          setLocalTurns((prev) =>
            prev.map((t) =>
              t.id === asstTurnId ? { ...t, pending: false, error: ev.message } : t,
            ),
          );
        }
      }
      settleTurn();
    } catch (err) {
      if (browseAbort?.signal.aborted) {
        patchTool({ status: "error", error: "cancelled" });
        setLocalTurns((prev) =>
          prev.map((t) =>
            t.id === asstTurnId
              ? { ...t, pending: false, error: "cancelled" }
              : t,
          ),
        );
      } else {
        const msg = err instanceof Error ? err.message : JSON.stringify(err);
        patchTool({ status: "error", error: msg });
        setLocalTurns((prev) =>
          prev.map((t) =>
            t.id === asstTurnId ? { ...t, pending: false, error: msg } : t,
          ),
        );
      }
    } finally {
      setBrowsing(false);
      browseAbort = null;
    }
  };

  const stop = () => {
    chatStream.stop();
    browseAbort?.abort();
  };

  // Pair user → assistant turns into single `.turn` blocks per the
  // locked HTML structure. Lone user turns (the assistant hasn't
  // started streaming yet) still render so the question is visible.
  const paired = createMemo(() => {
    const ts = turns();
    const pairs: Array<{ user: ChatTurn; assistant: ChatTurn | null }> = [];
    for (let i = 0; i < ts.length; i++) {
      const t = ts[i];
      if (!t) continue;
      if (t.role === "user") {
        const next = ts[i + 1];
        if (next && next.role === "assistant") {
          pairs.push({ user: t, assistant: next });
          i++;
        } else {
          pairs.push({ user: t, assistant: null });
        }
      } else if (t.role === "assistant") {
        // Assistant turn with no preceding user turn (rare) — show
        // with empty question slot.
        pairs.push({
          user: {
            id: `synthetic-${t.id}`,
            role: "user",
            content: "",
            reasoning: "",
            pending: false,
            tool_calls: [],
            retrieved_chunk_ids: [],
          },
          assistant: t,
        });
      }
    }
    return pairs;
  });

  // "turns" | "loading" | "empty". "loading" keeps the composer pinned at the
  // bottom while an existing thread's transcript is in flight (a switch),
  // instead of the centered "is-blank" new-conversation layout — which would
  // jolt the composer to center and back on every thread switch.
  const viewMode = (): ChatViewMode =>
    resolveChatViewMode(turns().length, props.threadId, transcript.loading);

  return (
    <div class="neuro-page is-bleed">
      <ConsentModal
        provider={pendingConsent()?.provider ?? null}
        onAccept={() => void acceptConsent()}
        onCancel={cancelConsent}
      />
      <div class="neuro-surface neuro-chat-surface">
        <Show when={viewMode() === "turns"} fallback={null}>
          <ol
            class="chat-feed neuro-turns"
            ref={messagesEl}
            onScroll={() => {
              scrollState.stickToBottom = isAtBottom();
            }}
          >
            <Show when={props.threadId ?? "draft"} keyed>
            <Index each={paired()}>
              {(pair) => (
                <TurnBlock
                  pair={pair()}
                  phaseLabel={
                    pair().assistant?.id === phaseTurnId()
                      ? phaseLabel(phase(), phaseProvider())
                      : null
                  }
                  totalMs={
                    pair().assistant?.id
                      ? turnTotals()[pair().assistant?.id ?? ""]
                      : undefined
                  }
                  reasoningOpen={reasoningOpen()}
                  onReasoningToggle={(open) => setReasoningOpen(open)}
                  onRetry={() => {
                    // Mirror send()'s own early-returns exactly. Arming the
                    // chip before a send() that bails would leave it stuck
                    // on, so the user's next ordinary message would silently
                    // fire a paid Exa search + consent prompt.
                    if (streaming() || creating || generatingImage() || browsing()) return;
                    // A persisted /search turn stores `contextText + text`.
                    // Re-sending that verbatim would push ~6 results of page
                    // text back through the composer; merely stripping it
                    // left the model answering blind, because send() disarms
                    // the chip on every send so no new search would run.
                    // Re-arm: ask the bare question, fetch fresh sources.
                    const plan = retryPlan(pair().user.content);
                    if (plan.rearmBrowse) {
                      setArmedProvider(null);
                      setImageModeEnabled(false);
                      setAgentModeEnabled(true);
                      setDraft(plan.draft);
                      void send();
                      return;
                    }
                    if (!plan.rearmSearch) {
                      setDraft(plan.draft);
                      void send();
                      return;
                    }
                    setImageModeEnabled(false);
                    setAgentModeEnabled(false);
                    setDraft(plan.draft);
                    if (isSearchProviderId(plan.provider)) {
                      // The block records its provider, so the retry re-runs
                      // the same search — one click, same grounding, same
                      // consent already given.
                      setArmedProvider(plan.provider);
                      void send();
                      return;
                    }
                    // Blocks written before provider identity was recorded
                    // cannot be attributed. Arm the default and hand control
                    // back rather than auto-sending: silently retrying a Web
                    // Agent turn through Exa would mislabel the tool-call
                    // card and could pop an Exa consent modal at a user who
                    // only consented to TinyFish.
                    setArmedProvider(RETRY_DEFAULT_PROVIDER);
                    focusComposer?.();
                  }}
                />
              )}
            </Index>
            </Show>
          </ol>
        </Show>

        <Show when={viewMode() === "loading"}>
          {/* Switching into an existing thread — show a quiet placeholder
              rather than the previous thread's stale turns or the centered
              empty layout, until the transcript resolves. */}
          <div class="neuro-thread-loading">loading thread…</div>
        </Show>

        <Show when={viewMode() === "empty"}>
          {/* Upper spacer — pushes the input toward viewport center. */}
          <div class="neuro-blank-spacer" />
        </Show>

        <div
          class={
            viewMode() === "empty"
              ? "neuro-bottom-input is-blank"
              : "neuro-bottom-input"
          }
        >
          {/* Attachment picker + library are both rendered hidden;
              the slash menu's /file item opens the library popover,
              which has an internal "upload new" button that triggers
              the OS file picker via attachmentTrigger. */}
          <AttachmentPicker
            store={attachments}
            conversationId={props.threadId}
            disabled={false}
            chipHidden
            triggerRef={(fn) => {
              attachmentTrigger = fn;
            }}
          />
          <AttachmentsLibrary
            store={attachments}
            disabled={streaming()}
            chipHidden
            triggerRef={(open) => {
              libraryTrigger = open;
            }}
            onUpload={() => attachmentTrigger?.()}
          />
          <InputShell
            value={draft()}
            onInput={setDraft}
            onSubmit={() => void send()}
            onSelectSlashItem={handleSlashSelect}
            onSelectProvider={handleSelectProvider}
            armedProvider={armedProvider()}
            focusRef={(fn) => {
              focusComposer = fn;
            }}
            leading={
              <VoiceRecorder
                isBlocked={() => props.overlayOpen?.() ?? false}
                onTranscript={appendTranscript}
              />
            }
            trailing={
              <>
                <Show when={armedProvider()}>
                  {(p) => (
                    <button
                      type="button"
                      class="neuro-mode-chip"
                      onClick={() => setArmedProvider(null)}
                      aria-label={`disarm ${labelFor(p())} search`}
                      title={`${labelFor(p())} armed — click to disarm`}
                    >
                      {labelFor(p())} ×
                    </button>
                  )}
                </Show>
                <Show when={imageModeEnabled()}>
                  <button
                    type="button"
                    class="neuro-mode-chip"
                    onClick={() => setImageModeEnabled(false)}
                    aria-label="disarm image mode"
                    title="image mode armed — click to disarm"
                  >
                    Image ×
                  </button>
                </Show>
                <Show when={agentModeEnabled()}>
                  <button
                    type="button"
                    class="neuro-mode-chip"
                    onClick={() => {
                      setAgentModeEnabled(false);
                      setBrowseError(null);
                    }}
                    aria-label="disarm Agent"
                    title="Agent armed — click to disarm"
                  >
                    Agent ×
                  </button>
                </Show>
                <Show when={browseError()}>
                  {(err) => <output class="chat-error">{err()}</output>}
                </Show>
                <Show when={streaming() || browsing()}>
                  <button type="button" onClick={stop} class="neuro-stop">
                    stop
                  </button>
                </Show>
              </>
            }
          />
        </div>
        <Show when={viewMode() === "empty"}>
          {/* Lower spacer + footer — balances the upper spacer so the
              input ends up vertically centered, and pins the footer to
              the bottom of the viewport. */}
          <div class="neuro-blank-spacer" />
          <div class="neuro-footer">
            <span class="neuro-footer-text">
              Chat runs on GPUs in California · Web search uses tinyfish.ai ·
              Image generation uses Gemini
            </span>
          </div>
        </Show>
      </div>
    </div>
  );
};

// Wall-clock start of each turn's thinking phase, keyed by the stable
// assistant turn id. Lives at module scope on purpose: `paired()` yields
// fresh object refs on every stream delta, so `<For>` tears down and
// recreates the streaming row's `TurnBlock` each chunk. A component-local
// signal would reset to 0 faster than the 1s interval can tick, pinning
// the elapsed counter at "0s" exactly on the prompts that DO stream
// reasoning. Anchoring the start here lets a remounted instance recompute
// the true elapsed immediately. Entry is removed when the turn leaves the
// thinking phase (content arrives / stream settles). Mirrors the existing
// `freshThreadIds` session-state pattern above.
const thinkingStartedAt = new Map<string, number>();

// Locked-HTML `.turn` block — question (small gray) above the bold
// answer headline, with optional prose body and reasoning row below.
const TurnBlock: Component<{
  pair: { user: ChatTurn; assistant: ChatTurn | null };
  /** Live phase label for this turn, or null when it is not in flight. */
  phaseLabel: { text: string; live: boolean } | null;
  /** Final elapsed once settled, so a slow turn can say how slow. */
  totalMs?: number;
  reasoningOpen: boolean;
  onReasoningToggle: (open: boolean) => void;
  onRetry?: () => void;
}> = (props) => {
  const split = createMemo(() =>
    splitAssistantContent(props.pair.assistant?.content ?? ""),
  );
  const parsedQuestion = createMemo(() =>
    parseUserContent(props.pair.user.content),
  );
  const reasoning = () => props.pair.assistant?.reasoning ?? "";
  const isStreaming = () => Boolean(props.pair.assistant?.pending);
  const thinking = () => isThinking(props.pair.assistant);

  // Live elapsed counter for the thinking state. Deterministic on every
  // turn (driven by `pending && empty content`, not by the flaky harmony
  // reasoning channel), so the user always gets a moving signal during
  // the medium-effort reasoning gap instead of a bare blinking caret.
  // Start time is anchored in `thinkingStartedAt` (module scope) so it
  // survives the per-delta `<For>` remount — the signal is seeded to the
  // already-elapsed value, not 0, so a remount mid-gap shows the right
  // number on the very next frame.
  const [thinkMs, setThinkMs] = createSignal(0);
  createEffect(() => {
    const asstId = props.pair.assistant?.id;
    if (!thinking() || !asstId) {
      if (asstId) thinkingStartedAt.delete(asstId);
      return;
    }
    const existing = thinkingStartedAt.get(asstId);
    const startedAt = existing ?? Date.now();
    if (existing === undefined) thinkingStartedAt.set(asstId, startedAt);
    // Tenths, so the displayed value actually moves. A 1s interval into a
    // tenths formatter rendered a frozen ".0" and read as a broken timer.
    const elapsed = () => Date.now() - startedAt;
    setThinkMs(elapsed());
    const tick = setInterval(() => setThinkMs(elapsed()), 100);
    onCleanup(() => clearInterval(tick));
  });

  return (
    <li class="neuro-turn-li">
      <div class="turn">
        <Show when={parsedQuestion().prompt.trim().length > 0}>
          <div class="turn-question">{parsedQuestion().prompt}</div>
        </Show>
        <Show when={props.pair.assistant}>
          {(asst) => (
            <>
              <Show
                when={thinking()}
                fallback={
                  <div class="turn-answer-headline">
                    <Show when={split().headline}>{split().headline}</Show>
                    <Show when={isStreaming()}>
                      <span class="stream-cursor" />
                    </Show>
                  </div>
                }
              >
                {/* <output> carries an implicit role=status (polite live
                    region) — it announces "Thinking" once when it appears.
                    The spark, dots, and per-second elapsed counter are all
                    aria-hidden so screen readers don't re-announce every
                    tick. */}
                <output class="turn-thinking">
                  <span class="thinking-spark" aria-hidden="true">
                    ✦
                  </span>
                  <span class="thinking-label">
                    {props.phaseLabel?.text ?? "Thinking"}
                  </span>
                  <span class="thinking-dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span class="thinking-elapsed" aria-hidden="true">
                    {formatElapsed(thinkMs())}
                  </span>
                </output>
              </Show>
              <Show when={split().body}>
                <div class="turn-body">
                  <StructuralRenderer source={split().body} />
                </div>
              </Show>
              <Show when={asst().tool_calls.length > 0}>
                <div class="chat-tool-calls">
                  <Index each={asst().tool_calls}>
                    {(call) => <ToolCallCard call={call()} />}
                  </Index>
                </div>
              </Show>
              <Show when={props.totalMs !== undefined && !isStreaming()}>
                <div class="turn-total muted">
                  {ANSWERED_IN} {formatElapsed(props.totalMs ?? 0)}
                </div>
              </Show>
              <Show when={reasoning().trim().length > 0}>
                <details
                  class="reasoning-row"
                  open={props.reasoningOpen}
                  onToggle={(e) => {
                    const next = (e.currentTarget as HTMLDetailsElement).open;
                    if (next !== props.reasoningOpen) {
                      props.onReasoningToggle(next);
                    }
                  }}
                >
                  <summary>
                    <span class="r-icon">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </span>
                    <span class="r-label">Reasoning</span>
                    <span class="r-summary">
                      — {reasoningPreview(reasoning())}
                    </span>
                  </summary>
                  <pre class="reasoning-detail">{reasoning()}</pre>
                </details>
              </Show>
              <Show when={asst().error}>
                {(err) => {
                  const failed = asst().tool_calls.find(
                    (c) => c.status === "error",
                  );
                  const title = failed
                    ? "tool call failed"
                    : "response interrupted";
                  const meta = failed
                    ? [
                        failed.server || failed.name,
                        failed.latency_ms != null
                          ? `${failed.latency_ms}ms`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : undefined;
                  return (
                    <ErrorBlock
                      title={title}
                      meta={meta}
                      detail={err()}
                      actions={
                        props.onRetry
                          ? [
                              {
                                label: "Retry",
                                onClick: () => props.onRetry?.(),
                              },
                            ]
                          : undefined
                      }
                    />
                  );
                }}
              </Show>
              <ContextUsedDisclosure chunkIds={asst().retrieved_chunk_ids} />
            </>
          )}
        </Show>
      </div>
    </li>
  );
};

function isNotFoundError(err: unknown): boolean {
  // apiFetch throws ApiRequestError on non-2xx; `code` is an own instance
  // property, read via duck-typing (not instanceof) so bare-ApiError throws
  // from the manual DELETE helpers match too. "not_found" is the BE 404
  // path. Network errors surface as plain Error and don't match.
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    (err as { code: unknown }).code === "not_found"
  );
}

function deriveThreadTitle(firstPrompt: string): string {
  // Take the first non-empty line, trim, cap at 80 chars. The BE caps
  // at 200; this is the FE's UX cap to keep the sidebar tidy. A future
  // LLM-titler can rewrite via PATCH /conversations/:id.
  const line = firstPrompt
    .split("\n")
    .map((s) => s.trim())
    .find((s) => s.length > 0);
  if (!line) return "new thread";
  return line.length > 80 ? `${line.slice(0, 77)}…` : line;
}

/**
 * Map a persisted tool call onto the view the card renders.
 *
 * `args` and `result` arrive as JSON strings. `args` is displayed verbatim in
 * a <pre>, so it stays a string; `result` is handed to the card's formatter,
 * which does better with structure — parsed when it can be, left as the raw
 * string when it cannot, so a malformed payload degrades to visible text
 * rather than an empty card.
 */
export function toToolCallView(c: PersistedToolCall): ToolCallView {
  let result: unknown;
  if (c.result !== undefined) {
    try {
      result = JSON.parse(c.result);
    } catch {
      result = c.result;
    }
  }
  return {
    id: c.id,
    name: c.name,
    server: c.server,
    args: c.args,
    // The BE derives exactly these three; anything else means a shape we do
    // not know how to render, so fall back to the neutral state.
    status:
      c.status === "ok" || c.status === "error" || c.status === "pending"
        ? c.status
        : "pending",
    result,
    error: c.error,
    latency_ms: c.latency_ms,
  };
}

function hydrateTurns(messages: MessageRecord[]): ChatTurn[] {
  // The BE persists every role; the UI only renders user/assistant.
  // Tool + system messages are filtered out — chat replays the visible
  // transcript, the BE re-runs tool calls on subsequent sends.
  //
  // Coalesce undefined → "". `reasoning_content` (and any future
  // serde-skipped string field) is omitted from the wire JSON when
  // empty due to `#[serde(skip_serializing_if = "String::is_empty")]`
  // on the BE, so the value can arrive as `undefined` even though the
  // ts-rs type says `string`. Concatenating that into a string later
  // would yield the literal "undefined".
  return messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => ({
      id: m.id,
      role: m.role as ChatTurn["role"],
      content: m.content ?? "",
      reasoning: m.reasoning_content ?? "",
      pending: false,
      // Rebuild tool-call cards from the persisted rows. This used to be
      // hardcoded `[]` because the wire carried nothing — so every card
      // vanished the instant a turn settled and the transcript refetched.
      tool_calls: (m.tool_calls ?? []).map(toToolCallView),
      // BE always serializes `retrieved_chunk_ids` as `[]` when empty
      // (see `feedback_serde_skip_serializing_fe_concat.md`), but
      // coalesce defensively in case future BE re-elides the field.
      retrieved_chunk_ids: m.retrieved_chunk_ids ?? [],
    }));
}

function relativeTime(rfc3339: string): string {
  const then = new Date(rfc3339).getTime();
  if (Number.isNaN(then)) return rfc3339;
  const delta = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (delta < 60) return "just now";
  if (delta < 3600) return `${Math.floor(delta / 60)}m`;
  if (delta < 86_400) return `${Math.floor(delta / 3600)}h`;
  return `${Math.floor(delta / 86_400)}d`;
}

export default ChatPage;
